import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE from '../config';
import Loading from './Loading';
import DeleteConfirmation from './DeleteConfirmation';
import ExportConfirmation from './ExportConfirmation';
import { useNavigate } from 'react-router-dom';
import SearchableDropdown from './SearchableDropdown';
import { useLanguage } from '../LanguageContext';

const Dispatch = () => {
  const navigate = useNavigate();
  const { t, isRtl } = useLanguage();
  const [dispatches, setDispatches] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [products, setProducts] = useState([]);
  const [packaging, setPackaging] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exportConfirm, setExportConfirm] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 720 : false);
  const [form, setForm] = useState({
    customerId: '',
    dispatch_date: new Date().toISOString().slice(0, 10),
    po_number: '',
    driver_name: '',
    car_number: '',
    region: '',
    products: [{ productId: '', quantity: 0 }],
    packagingMaterials: [{ receivedPackagingId: '', quantity: 0 }]
  });

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 720);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // close menu on outside click - Removed menuOpenId state
  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest('.floating-menu') && !e.target.closest('.menu-btn')) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest('.floating-menu') && !e.target.closest('.menu-btn')) setMenuOpenId(null);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dRes, cRes, pRes, pkRes, rRes] = await Promise.all([
        axios.get(`${API_BASE}/dispatchedproducts`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/customers`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/products`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/received-packaging`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/regions`, { headers: authHeaders() })
      ]);
      setDispatches(dRes.data || []);
      setCustomers(cRes.data || []);
      setProducts(pRes.data || []);
      setPackaging(pkRes.data || []);
      setRegions(rRes.data || []);
    } catch (err) {
      console.error(err);
      // alert('Failed to load dispatch data'); // Alert removed
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const filteredDispatches = dispatches.filter(d => {
    const term = searchQuery.toLowerCase();
    const dateText = d.dispatch_date ? new Date(d.dispatch_date).toLocaleDateString().toLowerCase() : '';
    const productsText = (d.products || []).map(p => p.productName || p.productId?.product_name || '').join(' ').toLowerCase();
    const packagingText = (d.packagingMaterials || []).map(pk => pk.receivedPackagingId?.packagingMaterialId?.name || '').join(' ').toLowerCase();

    return term === '' ||
      (d.po_number || d.dispatchId || d.uuid || '').toLowerCase().includes(term) ||
      (d.customerId?.customer_name || d.customerId?.name || '').toLowerCase().includes(term) ||
      (d.driver_name || '').toLowerCase().includes(term) ||
      (d.car_number || '').toLowerCase().includes(term) ||
      (d.region || '').toLowerCase().includes(term) ||
      dateText.includes(term) ||
      productsText.includes(term) ||
      packagingText.includes(term);
  });

  const downloadCSV = () => {
    const headers = [t.po_number, t.customer, t.date, t.driver, t.car, t.products, t.packaging];
    const rows = filteredDispatches.map(d => [
      `"${d.po_number || ''}"`,
      `"${d.customerId?.customer_name || d.customerId?.name || '-'}"`,
      `"${d.dispatch_date ? new Date(d.dispatch_date).toLocaleDateString(isRtl ? 'ar-EG' : 'en-US') : '-'}"`,
      `"${d.region || ''}"`,
      `"${d.driver_name || ''}"`,
      `"${d.car_number || ''}"`,
      `"${(d.products || []).map(p => `${p.productName || p.productId?.product_name || t.item}: ${Number(p.quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })}`).join('; ')}"`,
      `"${(d.packagingMaterials || []).map(pk => `${pk.receivedPackagingId?.packagingMaterialId?.name || t.pack}: ${Number(pk.quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })}`).join('; ')}"`
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `dispatch_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const addProductRow = () => setForm(f => ({ ...f, products: [...f.products, { productId: '', quantity: 0 }] }));
  const removeProductRow = (i) => setForm(f => { const copy = [...f.products]; copy.splice(i, 1); return { ...f, products: copy }; });
  const updateProductRow = (i, field, val) => setForm(f => { const copy = [...f.products]; copy[i] = { ...copy[i], [field]: val }; return { ...f, products: copy }; });

  const addPackRow = () => setForm(f => ({ ...f, packagingMaterials: [...f.packagingMaterials, { receivedPackagingId: '', quantity: 0 }] }));
  const removePackRow = (i) => setForm(f => { const copy = [...f.packagingMaterials]; copy.splice(i, 1); return { ...f, packagingMaterials: copy }; });
  const updatePackRow = (i, field, val) => setForm(f => { const copy = [...f.packagingMaterials]; copy[i] = { ...copy[i], [field]: val }; return { ...f, packagingMaterials: copy }; });

  const openAdd = () => {
    setIsEditing(false);
    setEditingId(null);
    setForm({ customerId: '', region: '', dispatch_date: new Date().toISOString().slice(0, 10), po_number: '', driver_name: '', car_number: '', products: [{ productId: '', quantity: 0 }], packagingMaterials: [{ receivedPackagingId: '', quantity: 0 }] });
    setModalOpen(true);
  };

  const openEdit = (d) => {
    setIsEditing(true);
    setEditingId(d._id);
    setForm({
      customerId: d.customerId?._id || d.customerId || '',
      dispatch_date: d.dispatch_date ? new Date(d.dispatch_date).toISOString().slice(0, 10) : '',
      po_number: d.po_number || '',
      region: d.region || '',
      driver_name: d.driver_name || '',
      car_number: d.car_number || '',
      products: (d.products || []).map(p => ({ productId: p.productId?._id || p.productId || '', quantity: p.quantity || 0, isOriginal: true })),
      packagingMaterials: (d.packagingMaterials || []).map(pk => ({ receivedPackagingId: pk.receivedPackagingId?._id || pk.receivedPackagingId || '', quantity: pk.quantity || 0, isOriginal: true }))
    });
    setModalOpen(true);
  };

  const handleDelete = (it) => {
    setDeleteConfirm({ open: true, id: it._id, name: it.po_number || it.dispatchId || it.uuid });
  };

  const executeDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE}/dispatchedproducts/${deleteConfirm.id}`, { headers: authHeaders() });
      await fetchAll();
      setDeleteConfirm({ open: false, id: null, name: '' });
    } catch (err) {
      alert(err.response?.data?.msg || t.delete_failed);
      setDeleteConfirm({ open: false, id: null, name: '' });
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customerId || !form.driver_name || !form.car_number || !form.po_number) { alert(t.fill_dispatch_fields); return; }

    const payload = {
      customerId: form.customerId,
      dispatch_date: form.dispatch_date,
      po_number: form.po_number,
      region: form.region,
      driver_name: form.driver_name,
      car_number: form.car_number,
      products: form.products.map(p => ({ productId: p.productId, quantity: Number(p.quantity || 0) })),
      packagingMaterials: form.packagingMaterials.map(p => ({ receivedPackagingId: p.receivedPackagingId, quantity: Number(p.quantity || 0) }))
    };

    setSaving(true);
    try {
      if (isEditing && editingId) {
        await axios.put(`${API_BASE}/dispatchedproducts/${editingId}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API_BASE}/dispatchedproducts`, payload, { headers: authHeaders() });
      }
      await fetchAll();
      setModalOpen(false);
    } catch (err) {
      alert(err.response?.data?.msg || t.dispatch_save_failed);
    } finally {
      setSaving(false);
    }
  };


  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 60, direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 16, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#1a1c1d' }}>{t.dispatch}</h1>
        <div style={{ display: 'flex', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
          <button onClick={() => setExportConfirm(true)} style={{
            padding: '8px 12px', borderRadius: 6, border: '1px solid #d2d2d2',
            background: '#fff', color: '#1a1c1d', fontWeight: 600, fontSize: 13, cursor: 'pointer'
          }}>{t.export}</button>
          <button onClick={openAdd} style={{
            padding: '8px 16px', borderRadius: 6, border: 'none',
            background: '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer'
          }}>{t.create_dispatch}</button>
        </div>
      </div>

      {/* IndexTable Card */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #ebebeb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden'
      }}>
        {/* Filter Bar */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f1f1', display: 'flex', gap: 8, flexWrap: 'wrap', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <span style={{ position: 'absolute', [isRtl ? 'right' : 'left']: 10, top: '50%', transform: 'translateY(-50%)', color: '#8c9196' }}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M19 19L14.65 14.65M17 9C17 13.4183 13.4183 17 9 17C4.58172 17 1 13.4183 1 9C1 4.58172 4.58172 1 9 1C13.4183 1 17 4.58172 17 9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            </span>
            <input
              placeholder={t.filter_dispatches}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%', padding: isRtl ? '8px 34px 8px 12px' : '8px 12px 8px 34px', borderRadius: 6, border: '1px solid #d2d2d2',
                fontSize: 13, color: '#1a1c1d', outline: 'none', textAlign: isRtl ? 'right' : 'left'
              }}
            />
          </div>
        </div>

        {loading ? (
          <Loading message={t.fetching_dispatches} />
        ) : (
          <div className="fade-in" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, direction: isRtl ? 'rtl' : 'ltr' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #ebebeb', height: 40 }}>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.po_number}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.customer}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.region}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.date}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.driver}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.car}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.products}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.packaging}</th>
                  <th style={{ textAlign: isRtl ? 'left' : 'right', padding: '10px 16px', fontWeight: 600 }}>{t.actions}</th>
                </tr>
              </thead>
              <tbody>
                {filteredDispatches.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding: 48, textAlign: 'center', color: '#6d7175' }}>{t.no_dispatches_found}</td></tr>
                ) : (
                  filteredDispatches.map((d) => (
                    <tr key={d._id} style={{
                      borderBottom: '1px solid #f1f1f1', cursor: 'pointer',
                      transition: 'background 0.2s'
                    }} onMouseEnter={(e) => e.currentTarget.style.background = '#f6f6f7'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '12px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{d.po_number || '-'}</td>
                      <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{d.customerId?.customer_name || d.customerId?.name || '-'}</td>
                      <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{d.region || '-'}</td>
                      <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{d.dispatch_date ? new Date(d.dispatch_date).toLocaleDateString(isRtl ? 'ar-EG' : 'en-US') : '-'}</td>
                      <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{d.driver_name}</td>
                      <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{d.car_number}</td>
                      <td style={{ padding: '12px 16px', fontSize: 12, textAlign: isRtl ? 'right' : 'left' }}>
                        {(d.products || []).map((p, i) => (
                          <div key={i}>{p.productName || p.productId?.product_name || t.item}: {Number(p.quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })}</div>
                        ))}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, textAlign: isRtl ? 'right' : 'left' }}>
                        {(d.packagingMaterials || []).map((pk, i) => (
                          <div key={i}>{pk.receivedPackagingId?.packagingMaterialId?.name || t.pack}: {Number(pk.quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })}</div>
                        ))}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right' }} onClick={(e) => e.stopPropagation()}>
                        <button className="menu-btn" onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          setMenuPos({ x: isRtl ? rect.right : rect.left, y: rect.bottom + 8 });
                          setMenuOpenId(menuOpenId === d._id ? null : d._id);
                        }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6d7175', padding: 4 }}>
                          <svg width="20" height="20" viewBox="0 0 20 20"><path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0z" fill="currentColor" /></svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dispatch Modal */}
      {modalOpen && (
        <div className="overlay-fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, backdropFilter: 'blur(2px)', padding: isMobile ? 12 : 0 }}>
          <div className="modal-fade-in" style={{ width: 800, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 12, padding: isMobile ? 16 : 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{isEditing ? t.edit_dispatch : t.create_dispatch}</h2>
              <button onClick={() => setModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6d7175' }}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>

              {/* Products Section — moved to top */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                  <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{t.products}</h3>
                  <button type="button" onClick={addProductRow} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d2d2d2', background: '#fff', fontSize: 12, cursor: 'pointer' }}>+ {t.add_product}</button>
                </div>
                {(form.products || []).map((p, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: isMobile ? 'wrap' : 'nowrap', opacity: p.isOriginal && isEditing ? 0.85 : 1, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                    <SearchableDropdown
                      showCheckbox={false}
                      disabled={p.isOriginal && isEditing}
                      options={products.map(pr => {
                        const usedInOtherRows = (form.products || []).reduce((acc, curr, i) => {
                          if (i === idx) return acc;
                          if (curr.productId === pr._id) return acc + Number(curr.quantity || 0);
                          return acc;
                        }, 0);
                        const avail = parseFloat(((pr.product_quantiry ?? pr.product_quantity ?? 0) - usedInOtherRows).toFixed(2));
                        return {
                          value: pr._id,
                          label: `${pr.product_name} (Batch: ${pr.batch}) (Avail: ${avail} kg)`
                        };
                      })}
                      value={p.productId}
                      onChange={(val) => updateProductRow(idx, 'productId', val)}
                      placeholder={t.select_product}
                      style={{ flex: isMobile ? '1 1 100%' : 2 }}
                    />
                    <div style={{ position: 'relative', flex: 1, textAlign: isRtl ? 'right' : 'left' }}>
                      <input
                        type="number"
                        step="any"
                        placeholder={t.quantity}
                        value={p.quantity}
                        disabled={p.isOriginal && isEditing}
                        onChange={(e) => updateProductRow(idx, 'quantity', e.target.value)}
                        style={{
                          width: '100%', padding: '8px 12px', borderRadius: 6,
                          border: `1px solid ${p.productId && !p.isOriginal && products.find(pr => pr._id === p.productId)?.product_quantiry < Number(p.quantity) ? '#cf1322' : '#d2d2d2'}`,
                          background: p.isOriginal && isEditing ? '#f3f4f6' : (p.productId && !p.isOriginal && products.find(pr => pr._id === p.productId)?.product_quantiry < Number(p.quantity) ? '#fff1f0' : '#fff'),
                          cursor: p.isOriginal && isEditing ? 'not-allowed' : 'text',
                          color: p.isOriginal && isEditing ? '#6d7175' : '#1a1c1d',
                          textAlign: isRtl ? 'right' : 'left'
                        }}
                        min="0"
                      />
                      {p.isOriginal && isEditing && (
                        <div style={{ color: '#6d7175', fontSize: 10, marginTop: 2, fontStyle: 'italic' }}>{t.locked_remove_change}</div>
                      )}
                      {p.productId && !p.isOriginal && products.find(pr => pr._id === p.productId)?.product_quantiry < Number(p.quantity) && (
                        <div style={{ fontSize: 10, color: '#cf1322', marginTop: 2, position: 'absolute', top: '100%', [isRtl ? 'right' : 'left']: 0 }}>
                          {t.available}: {products.find(pr => pr._id === p.productId)?.product_quantiry} kg
                        </div>
                      )}
                    </div>
                    <button type="button" onClick={() => removeProductRow(idx)} style={{ padding: '8px', background: 'none', border: '1px solid #d2d2d2', color: '#bf0711', borderRadius: 6, cursor: 'pointer' }}>&times;</button>
                  </div>
                ))}
              </div>

              {/* Packaging Section — moved to top */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                  <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{t.packaging_materials}</h3>
                  <button type="button" onClick={addPackRow} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d2d2d2', background: '#fff', fontSize: 12, cursor: 'pointer' }}>+ {t.add_packaging}</button>
                </div>
                {(form.packagingMaterials || []).map((pm, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: isMobile ? 'wrap' : 'nowrap', opacity: pm.isOriginal && isEditing ? 0.85 : 1, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                    <SearchableDropdown
                      showCheckbox={false}
                      disabled={pm.isOriginal && isEditing}
                      options={packaging.map(pk => {
                        const usedInOtherRows = (form.packagingMaterials || []).reduce((acc, curr, i) => {
                          if (i === idx) return acc;
                          if (curr.receivedPackagingId === pk._id) return acc + Number(curr.quantity || 0);
                          return acc;
                        }, 0);
                        const avail = parseFloat(((pk.quantity || 0) - usedInOtherRows).toFixed(2));
                        return {
                          value: pk._id,
                          label: `${pk.packagingMaterialId?.name} (${pk.supplierId?.supplier_name}) - Avail: ${avail}`
                        };
                      })}
                      value={pm.receivedPackagingId}
                      onChange={(val) => updatePackRow(idx, 'receivedPackagingId', val)}
                      placeholder={t.select_batch}
                      style={{ flex: isMobile ? '1 1 100%' : 2 }}
                    />
                    <div style={{ position: 'relative', flex: 1, textAlign: isRtl ? 'right' : 'left' }}>
                      <input
                        type="number"
                        step="any"
                        placeholder={t.quantity}
                        value={pm.quantity}
                        disabled={pm.isOriginal && isEditing}
                        onChange={(e) => updatePackRow(idx, 'quantity', e.target.value)}
                        style={{
                          width: '100%', padding: '8px 12px', borderRadius: 6,
                          border: `1px solid ${pm.receivedPackagingId && !pm.isOriginal && packaging.find(m => m._id === pm.receivedPackagingId)?.quantity < Number(pm.quantity) ? '#cf1322' : '#d2d2d2'}`,
                          background: pm.isOriginal && isEditing ? '#f3f4f6' : (pm.receivedPackagingId && !pm.isOriginal && packaging.find(m => m._id === pm.receivedPackagingId)?.quantity < Number(pm.quantity) ? '#fff1f0' : '#fff'),
                          cursor: pm.isOriginal && isEditing ? 'not-allowed' : 'text',
                          color: pm.isOriginal && isEditing ? '#6d7175' : '#1a1c1d',
                          textAlign: isRtl ? 'right' : 'left'
                        }}
                        min="0"
                      />
                      {pm.isOriginal && isEditing && (
                        <div style={{ color: '#6d7175', fontSize: 10, marginTop: 2, fontStyle: 'italic' }}>{t.locked_remove_change}</div>
                      )}
                      {pm.receivedPackagingId && !pm.isOriginal && packaging.find(m => m._id === pm.receivedPackagingId)?.quantity < Number(pm.quantity) && (
                        <div style={{ fontSize: 10, color: '#cf1322', marginTop: 2, position: 'absolute', top: '100%', [isRtl ? 'right' : 'left']: 0 }}>
                          {t.available}: {packaging.find(m => m._id === pm.receivedPackagingId)?.quantity} kg
                        </div>
                      )}
                    </div>
                    <button type="button" onClick={() => removePackRow(idx)} style={{ padding: '8px', background: 'none', border: '1px solid #d2d2d2', color: '#bf0711', borderRadius: 6, cursor: 'pointer' }}>&times;</button>
                  </div>
                ))}
              </div>

              {/* Metadata Fields — moved to bottom */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 24 }}>
                <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t.customer}</label>
                  <SearchableDropdown
                    showCheckbox={false}
                    options={customers.map(c => ({ value: c._id, label: c.customer_name || c.name }))}
                    value={form.customerId}
                    onChange={(val) => setForm(f => ({ ...f, customerId: val }))}
                    placeholder={t.select_customer}
                  />
                </div>
                <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t.region}</label>
                  <SearchableDropdown
                    showCheckbox={false}
                    options={regions.map(r => ({ value: r.name, label: r.name }))}
                    value={form.region}
                    onChange={(val) => setForm(f => ({ ...f, region: val }))}
                    placeholder={t.select_region}
                  />
                </div>
                <div style={{ ... (isMobile ? { gridColumn: 'span 2' } : {}), textAlign: isRtl ? 'right' : 'left' }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t.date}</label>
                  <input type="date" value={form.dispatch_date} onChange={(e) => setForm(f => ({ ...f, dispatch_date: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d2d2d2', textAlign: isRtl ? 'right' : 'left' }} />
                </div>
                <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t.driver_name}</label>
                  <input value={form.driver_name} onChange={(e) => setForm(f => ({ ...f, driver_name: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d2d2d2', textAlign: isRtl ? 'right' : 'left' }} />
                </div>
                <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t.car_number}</label>
                  <input value={form.car_number} onChange={(e) => setForm(f => ({ ...f, car_number: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d2d2d2', textAlign: isRtl ? 'right' : 'left' }} />
                </div>
                <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t.po_number}</label>
                  <input value={form.po_number} onChange={(e) => setForm(f => ({ ...f, po_number: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d2d2d2', textAlign: isRtl ? 'right' : 'left' }} required />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, borderTop: '1px solid #ebebeb', paddingTop: 20, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                <button type="button" onClick={() => setModalOpen(false)} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #d2d2d2', background: '#fff' }}>{t.cancel_btn}</button>
                <button
                  type="submit"
                  disabled={
                    form.products.some(p => p.productId && products.find(pr => pr._id === p.productId)?.product_quantiry < Number(p.quantity)) ||
                    form.packagingMaterials.some(pm => pm.receivedPackagingId && packaging.find(m => m._id === pm.receivedPackagingId)?.quantity < Number(pm.quantity))
                  }
                  style={{
                    padding: '8px 16px', borderRadius: 6, border: 'none',
                    background: (
                      form.products.some(p => p.productId && products.find(pr => pr._id === p.productId)?.product_quantiry < Number(p.quantity)) ||
                      form.packagingMaterials.some(pm => pm.receivedPackagingId && packaging.find(m => m._id === pm.receivedPackagingId)?.quantity < Number(pm.quantity))
                    ) ? '#ccc' : '#1976d2',
                    color: '#fff', fontWeight: 600,
                    cursor: (
                      saving ||
                      form.products.some(p => p.productId && products.find(pr => pr._id === p.productId)?.product_quantiry < Number(p.quantity)) ||
                      form.packagingMaterials.some(pm => pm.receivedPackagingId && packaging.find(m => m._id === pm.receivedPackagingId)?.quantity < Number(pm.quantity))
                    ) ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 140
                  }}
                >
                  {saving ? <div className="dots-loading"><span></span><span></span><span></span></div> : (isEditing ? t.save_changes : t.create_dispatch)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Floating Menu */}
      {menuOpenId && (
        <div className="floating-menu" style={{
          position: 'fixed',
          [isRtl ? 'right' : 'left']: isRtl ? (window.innerWidth - menuPos.x) : (menuPos.x - 120),
          top: menuPos.y, background: '#fff',
          borderRadius: 8, boxShadow: '0 12px 30px rgba(0,0,0,0.15)', minWidth: 160, zIndex: 2000,
          border: '1px solid #ebebeb'
        }} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', flexDirection: 'column', padding: 4 }}>
            <button onClick={() => { const d = dispatches.find(i => i._id === menuOpenId); setMenuOpenId(null); if (d) openEdit(d); }} style={{
              padding: '8px 12px', textAlign: isRtl ? 'right' : 'left', border: 'none', background: 'transparent',
              cursor: 'pointer', color: '#1a1c1d', fontSize: 13, borderRadius: 4
            }} onMouseOver={e => e.target.style.background = '#f1f1f1'} onMouseOut={e => e.target.style.background = 'transparent'}>{t.edit}</button>
            <button onClick={() => { const d = dispatches.find(i => i._id === menuOpenId); setMenuOpenId(null); if (d) handleDelete(d); }} style={{
              padding: '8px 12px', textAlign: isRtl ? 'right' : 'left', border: 'none', background: 'transparent',
              cursor: 'pointer', color: '#d82c0d', fontSize: 13, borderRadius: 4
            }} onMouseOver={e => e.target.style.background = '#f1f1f1'} onMouseOut={e => e.target.style.background = 'transparent'}>{t.delete}</button>
          </div>
        </div>
      )}
      {deleteConfirm.open && (
        <DeleteConfirmation
          itemName={deleteConfirm.name}
          onConfirm={executeDelete}
          onCancel={() => setDeleteConfirm({ open: false, id: null, name: '' })}
          isDeleting={deleting}
        />
      )}
      {exportConfirm && (
        <ExportConfirmation
          itemName={t.dispatch}
          onConfirm={() => { downloadCSV(); setExportConfirm(false); }}
          onCancel={() => setExportConfirm(false)}
        />
      )}
    </div>
  );
};

export default Dispatch;
